import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, Info } from 'lucide-react';
import { EDIT_REASONS } from './workbenchShared';

// Bulk Normalize Dialog (Feb 2026) — apply one hours rule across selected workers.
export const BulkNormalizeDialog = ({
  open, onClose, form, updateForm, selectedCount, startDate, endDate,
  selectionDays, preview, onSubmit, saving,
}) => (
  <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-w-2xl" data-testid="pw-bulk-dialog" onCloseAutoFocus={(e) => e.preventDefault()}>
      <DialogHeader>
        <DialogTitle>Bulk normalize hours</DialogTitle>
        <DialogDescription>
          Apply a single rule across <b>{selectedCount}</b> selected worker{selectedCount === 1 ? '' : 's'} in the pay period {startDate} → {endDate}.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4">
        {/* Mode selector */}
        <div className="space-y-1">
          <Label className="text-xs">Target hours</Label>
          <Select
            value={form.mode}
            onValueChange={(v) => updateForm({ mode: v })}
          >
            <SelectTrigger data-testid="pw-bulk-mode"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="apply_shift_duration" data-testid="pw-bulk-mode-shift">
                Each worker&apos;s scheduled shift duration (recommended)
              </SelectItem>
              <SelectItem value="fixed_hours" data-testid="pw-bulk-mode-fixed">
                Fixed hours for every session (e.g. 8h for all)
              </SelectItem>
            </SelectContent>
          </Select>
          {form.mode === 'apply_shift_duration' && (
            <p className="text-[11px] text-slate-500">
              Uses each worker&apos;s configured shift hours (defaults to 8h if not set). Best when crews are on different schedules.
            </p>
          )}
        </div>

        {/* Fixed-hours input */}
        {form.mode === 'fixed_hours' && (
          <div className="space-y-1">
            <Label className="text-xs">Hours to apply</Label>
            <Input
              type="number"
              min="0.25"
              max="24"
              step="0.25"
              value={form.fixed_hours}
              onChange={(e) => updateForm({ fixed_hours: e.target.value })}
              data-testid="pw-bulk-fixed-hours"
              className="w-32"
            />
            <p className="text-[11px] text-slate-500">
              Every selected session is set to <b>clock-in + {form.fixed_hours || '?'}h</b>. Use this for &ldquo;everyone gets exactly 8h today&rdquo;.
            </p>
          </div>
        )}

        {/* Day filter */}
        <div className="space-y-1">
          <Label className="text-xs">Apply to</Label>
          <Select
            value={form.day_filter || 'all'}
            onValueChange={(v) => updateForm({ day_filter: v === 'all' ? '' : v })}
          >
            <SelectTrigger data-testid="pw-bulk-day-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="pw-bulk-day-all">Entire pay period</SelectItem>
              {selectionDays.map((d) => (
                <SelectItem key={d} value={d} data-testid={`pw-bulk-day-${d}`}>
                  Just {d}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Audit reason */}
        <div className="space-y-1">
          <Label className="text-xs">Reason (audit) *</Label>
          <Select
            value={form.reason_kind}
            onValueChange={(v) => updateForm({ reason_kind: v })}
          >
            <SelectTrigger data-testid="pw-bulk-reason-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {EDIT_REASONS.map((r) => (
                <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <Label className="text-xs">Notes (optional)</Label>
          <Textarea
            rows={2}
            placeholder="Anything else HR should see (e.g. 'Approved by Corey 6/14')…"
            value={form.notes}
            onChange={(e) => updateForm({ notes: e.target.value })}
            data-testid="pw-bulk-notes"
          />
        </div>

        {/* Preview banner */}
        <div
          className="p-2 rounded border border-blue-700/40 bg-blue-500/5 text-xs text-blue-200"
          data-testid="pw-bulk-preview"
        >
          <Info className="w-3 h-3 inline mr-1" />
          Will adjust <b>{preview?.entries.length || 0}</b> session{preview?.entries.length === 1 ? '' : 's'} across <b>{preview?.workers.length || 0}</b> worker{preview?.workers.length === 1 ? '' : 's'}.
          {preview && preview.entries.length === 0 && (
            <span className="block mt-1 text-amber-300">No sessions match the current filter — try widening the day filter.</span>
          )}
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onClose} data-testid="pw-bulk-cancel">Cancel</Button>
        <Button
          onClick={onSubmit}
          disabled={saving || !preview || preview.entries.length === 0}
          className="bg-[#1E88E5] hover:bg-blue-700"
          data-testid="pw-bulk-submit"
        >
          {saving
            ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Applying…</>
            : <>Apply to {preview?.entries.length || 0} session{preview?.entries.length === 1 ? '' : 's'}</>}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
